import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider
} from '@mui/material'
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Block as BlockIcon
} from '@mui/icons-material'
import { blockedSendersService, BlockedSender } from '../../../../shared/services/BlockedSendersService'

export function BlockedSenders() {
  const [blockedSenders, setBlockedSenders] = useState<BlockedSender[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [addBlockType, setAddBlockType] = useState<'Email' | 'Domain'>('Email')
  const [addValue, setAddValue] = useState('')
  const [addNotes, setAddNotes] = useState('')
  const [addLoading, setAddLoading] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)

  const [deleteTarget, setDeleteTarget] = useState<BlockedSender | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  useEffect(() => {
    loadBlockedSenders()
  }, [])

  const loadBlockedSenders = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await blockedSendersService.getBlockedSenders()
      setBlockedSenders(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load blocked senders')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = async () => {
    if (!addValue.trim()) {
      setAddError('Please enter an email address or domain.')
      return
    }
    try {
      setAddLoading(true)
      setAddError(null)
      await blockedSendersService.addBlockedSender({
        blockType: addBlockType,
        value: addValue.trim(),
        notes: addNotes.trim() || undefined
      })
      setAddDialogOpen(false)
      setAddValue('')
      setAddNotes('')
      setAddBlockType('Email')
      setSuccess(`${addBlockType} blocked successfully.`)
      await loadBlockedSenders()
    } catch (err: any) {
      setAddError(err.message || 'Failed to add blocked sender.')
    } finally {
      setAddLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      setDeleteLoading(true)
      await blockedSendersService.removeBlockedSender(deleteTarget.blockedSenderId)
      setDeleteTarget(null)
      setSuccess(`'${deleteTarget.value}' removed from blocked senders.`)
      await loadBlockedSenders()
    } catch (err: any) {
      setError(err.message || 'Failed to remove blocked sender.')
    } finally {
      setDeleteLoading(false)
    }
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Blocked Senders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Emails from blocked addresses or domains will be silently discarded — no ticket created.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setAddDialogOpen(true); setAddError(null) }}
        >
          Block Sender
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : blockedSenders.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <BlockIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No blocked senders. Emails from all senders will be processed.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Blocked</TableCell>
                <TableCell align="right">Remove</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {blockedSenders.map((sender) => (
                <TableRow key={sender.blockedSenderId} hover>
                  <TableCell>
                    <Chip
                      label={sender.blockType}
                      size="small"
                      color={sender.blockType === 'Domain' ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {sender.value}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {sender.notes || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(sender.createdAt).toLocaleDateString('en-GB')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Remove block">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(sender)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Block a Sender</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel>Block Type</InputLabel>
              <Select
                value={addBlockType}
                label="Block Type"
                onChange={(e) => setAddBlockType(e.target.value as 'Email' | 'Domain')}
              >
                <MenuItem value="Email">Email address — block one specific sender</MenuItem>
                <MenuItem value="Domain">Domain — block all senders from a domain</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={addBlockType === 'Email' ? 'Email Address' : 'Domain'}
              placeholder={addBlockType === 'Email' ? 'e.g. wendy224@groirl.com' : 'e.g. groirl.com'}
              value={addValue}
              onChange={(e) => setAddValue(e.target.value)}
              fullWidth
            />
            <TextField
              label="Notes (optional)"
              placeholder="e.g. Spam — blocked via TKT-2026-000010"
              value={addNotes}
              onChange={(e) => setAddNotes(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
            {addError && <Alert severity="error">{addError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={addLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={addLoading} startIcon={addLoading ? <CircularProgress size={16} /> : <BlockIcon />}>
            Block
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Block</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{deleteTarget?.value}</strong> from the blocked list?
            Future emails from this {deleteTarget?.blockType === 'Domain' ? 'domain' : 'address'} will be processed normally.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={16} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
